import React, { useState, useEffect } from "react";
import {
  Button,
  Icon,
  Label,
  Image,
  Confirm,
  Form,
  TextArea,
  Loader,
  Segment,
  Header,
  Select,
  Divider,
  Grid,
} from "semantic-ui-react";
import { RegisteredPayment } from "../../../api";
import { useAuth } from "../../../hooks";
import { BasicModal, CustomTable } from "../../Shared";
import { buildStorageUrl } from "../../../utils";
import Swal from "sweetalert2";
import "./PaymentsList.scss";

const registeredPaymentController = new RegisteredPayment();

export function PaymentsList() {
  const { accessToken } = useAuth();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [statusFilter, setStatusFilter] = useState("1"); // 1 = PENDING by default

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState(null);

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showConfirmAccept, setShowConfirmAccept] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [submittingAction, setSubmittingAction] = useState(false);

  const onReload = () => setReload((prev) => !prev);

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const response = await registeredPaymentController.getPayments(
          accessToken,
          {
            status: statusFilter,
            page: currentPage,
            limit: itemsPerPage,
          },
        );
        setPayments(response?.payments || []);
        setPagination(response?.pagination || {
          total: response?.payments?.length || 0,
          pages: 1,
          page: currentPage,
          limit: itemsPerPage,
        });
      } catch (error) {
        console.error("Error al obtener lista de pagos", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [reload, statusFilter, accessToken, currentPage, itemsPerPage]);

  const handleOpenVoucher = (payment) => {
    setSelectedPayment(payment);
    setShowVoucherModal(true);
  };

  const handleOpenAccept = (payment) => {
    setSelectedPayment(payment);
    setShowConfirmAccept(true);
  };

  const handleOpenReject = (payment) => {
    setSelectedPayment(payment);
    setRejectionReason("");
    setShowRejectModal(true);
  };

  const handleAccept = async () => {
    try {
      if (!selectedPayment) return;
      setSubmittingAction(true);
      await registeredPaymentController.confirmPayment(
        accessToken,
        selectedPayment.id,
        {},
      );
      setShowConfirmAccept(false);
      Swal.fire({
        icon: "success",
        title: "Pago Confirmado",
        text: "El pago ha sido aprobado e ingresado a caja de forma correcta.",
        background: "#1f2937",
        color: "#fff",
        confirmButtonColor: "#3b82f6",
      });
      onReload();
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "No se pudo confirmar el pago.",
        background: "#1f2937",
        color: "#fff",
      });
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleReject = async () => {
    try {
      if (!selectedPayment) return;
      if (!rejectionReason.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Advertencia",
          text: "Debe ingresar un motivo para el rechazo del pago.",
          background: "#1f2937",
          color: "#fff",
        });
        return;
      }
      setSubmittingAction(true);
      await registeredPaymentController.confirmPayment(
        accessToken,
        selectedPayment.id,
        {
          rejectionReason,
        },
      );
      setShowRejectModal(false);
      Swal.fire({
        icon: "success",
        title: "Pago Rechazado",
        text: "El reporte de pago ha sido rechazado y notificado al estudiante.",
        background: "#1f2937",
        color: "#fff",
        confirmButtonColor: "#ef4444",
      });
      onReload();
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "No se pudo rechazar el pago.",
        background: "#1f2937",
        color: "#fff",
      });
    } finally {
      setSubmittingAction(false);
    }
  };

  const statusOptions = [
    { key: "all", value: "", text: "Selecciona un estado" },
    { key: "pending", value: "1", text: "Pendientes de validación" },
    { key: "confirmed", value: "2", text: "Confirmados / Aprobados" },
    { key: "rejected", value: "3", text: "Rechazados" },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "blue";
      case 2:
        return "green";
      case 3:
        return "red";
      default:
        return "grey";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return "PENDIENTE";
      case 2:
        return "CONFIRMADO";
      case 3:
        return "RECHAZADO";
      default:
        return "SIN DEFINIR";
    }
  };

  const getPaymentMethodText = (method) => {
    switch (method) {
      case 1:
        return "Efectivo";
      case 2:
        return "Yape";
      case 3:
        return "Plin";
      case 4:
        return "Transferencia bancaria";
      case 5:
        return "Otros";
      default:
        return "Sin definir";
    }
  };

  const getMonthName = (monthNumber) => {
    if (!monthNumber) return "";
    const months = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];
    return months[monthNumber - 1] || "";
  };

  const tableConfig = {
    headers: [
      "Estudiante",
      "Concepto",
      "Monto Cuota",
      "Monto Mora",
      "Monto Total",
      "F. Pago",
      "Método",
      "Estado",
      "Acciones",
    ],
    fields: [
      {
        field: "Student",
        component: "custom",
        render: (payment) => (
          <span className="font-bold">
            {payment.Student?.User
              ? `${payment.Student.User.firstname} ${payment.Student.User.lastname}`
              : `Estudiante #${payment.studentId}`}
          </span>
        ),
      },
      {
        field: "PaymentConcept.name",
        dataType: "text",
      },
      {
        field: "tuitionAmount",
        component: "custom",
        align: "right",
        render: (payment) => `S/ ${Number(payment.tuitionAmount || 0).toFixed(2)}`,
      },
      {
        field: "lateFeeAmount",
        component: "custom",
        align: "right",
        render: (payment) => `S/ ${Number(payment.lateFeeAmount || 0).toFixed(2)}`,
      },
      {
        field: "totalAmount",
        component: "custom",
        align: "right",
        render: (payment) => (
          <span className="font-bold">
            S/ {Number(payment.totalAmount || 0).toFixed(2)}
          </span>
        ),
      },
      {
        field: "paymentDate",
        component: "custom",
        align: "center",
        render: (payment) => payment.paymentDate
          ? new Date(payment.paymentDate).toLocaleDateString()
          : "N/A",
      },
      {
        field: "paymentMethod",
        component: "custom",
        align: "center",
        render: (payment) => getPaymentMethodText(payment.paymentMethod),
      },
      {
        field: "status",
        component: "Label",
        align: "center",
        getColor: getStatusColor,
        getText: getStatusText,
      },
      {
        field: "actions",
        component: "custom",
        align: "center",
        render: (payment) => (
          <div className="payments-list__actions">
            <Button.Group size="mini">
              <Button
                color="blue"
                icon="eye"
                title="Ver Comprobante"
                onClick={() => handleOpenVoucher(payment)}
              />
              {payment.status === 1 && (
                <>
                  <Button
                    color="green"
                    icon="check"
                    title="Confirmar Pago"
                    onClick={() => handleOpenAccept(payment)}
                  />
                  <Button
                    color="red"
                    icon="cancel"
                    title="Rechazar Pago"
                    onClick={() => handleOpenReject(payment)}
                  />
                </>
              )}
            </Button.Group>
          </div>
        ),
      },
    ],
    columnWidths: ["20%", "15%", "10%", "10%", "10%", "10%", "10%", "10%", "10%"],
  };

  return (
    <div className="payments-list">
      <div className="payments-list__header">
        <Header as="h2">
          <Icon name="check" color="blue" />
          <Header.Content>
            Validación de Comprobantes de Pago
            <Header.Subheader>
              Revisa y confirma los reportes de pago enviados por los
              estudiantes
            </Header.Subheader>
          </Header.Content>
        </Header>
        <div className="payments-list__filters">
          <Select
            placeholder="Selecciona un estado"
            clearable
            value={statusFilter}
            options={statusOptions}
            onChange={(e, { value }) => setStatusFilter(value)}
            className="edunova-select"
          />
        </div>
      </div>

      {loading ? (
        <Loader
          active
          inline="centered"
          content="Cargando reportes de pago..."
        />
      ) : payments.length === 0 ? (
        <Segment
          placeholder
          textAlign="center"
          className="payments-list__empty"
        >
          <Header icon>
            <Icon name="search" color="grey" />
            No se encontraron reportes de pago con el estado seleccionado.
          </Header>
        </Segment>
      ) : (
        <div className="payments-list__table-wrapper">
          <CustomTable
            color="blue"
            tableConfig={tableConfig}
            data={payments}
            pagination={pagination}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            onReload={onReload}
          />
        </div>
      )}

      {/* Voucher View Modal */}
      <BasicModal
        title={`Comprobante de ${selectedPayment?.Student?.User ? `${selectedPayment.Student.User.firstname} ${selectedPayment.Student.User.lastname}` : `Estudiante #${selectedPayment?.studentId}`}`}
        show={showVoucherModal}
        close={() => setShowVoucherModal(false)}
        size="small"
      >
        <div style={{ textAlign: "center", padding: "1rem" }}>
          {selectedPayment?.path ? (
            <Image
              src={buildStorageUrl(selectedPayment.path)}
              rounded
              fluid
              centered
              style={{ maxHeight: "70vh", objectFit: "contain" }}
            />
          ) : (
            <p>No hay voucher adjunto para este pago.</p>
          )}
          <Divider />
          <Grid columns={2}>
            <Grid.Column>
              <strong>Código Transacción:</strong>{" "}
              {selectedPayment?.transactionCode || "N/A"}
            </Grid.Column>
            <Grid.Column>
              <strong>Monto Total:</strong> S/{" "}
              {Number(selectedPayment?.totalAmount || 0).toFixed(2)}
            </Grid.Column>
          </Grid>
        </div>
      </BasicModal>

      {/* Accept Payment Confirm */}
      <Confirm
        open={showConfirmAccept}
        onCancel={() => setShowConfirmAccept(false)}
        onConfirm={handleAccept}
        title="Confirmar Recepción de Pago"
        confirmButton={
          submittingAction ? "Procesando..." : "Confirmar e Ingresar"
        }
        cancelButton="Cancelar"
        size="tiny"
        content={
          <div style={{ padding: "1.5rem" }}>
            <p>
              ¿Estás seguro de confirmar y aprobar este pago de{" "}
              <strong>
                S/ {Number(selectedPayment?.totalAmount || 0).toFixed(2)}
              </strong>
              ?
            </p>
            <p>
              Esto registrará automáticamente el ingreso en la caja activa y
              actualizará el saldo deudor del estudiante.
            </p>
          </div>
        }
      />

      {/* Reject Payment Modal */}
      <BasicModal
        title="Rechazar Comprobante de Pago"
        show={showRejectModal}
        close={() => setShowRejectModal(false)}
        size="small"
      >
        <div style={{ padding: "1rem" }}>
          <p>
            ¿Está seguro de que desea rechazar el comprobante enviado por el
            estudiante?
          </p>
          <Form onSubmit={handleReject}>
            <Form.Field>
              <label
                style={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "0.5rem",
                }}
              >
                Motivo del Rechazo:
              </label>
              <TextArea
                rows={3}
                placeholder="Escriba el motivo detallado de por qué se rechaza el pago para que el estudiante pueda corregirlo..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                required
              />
            </Form.Field>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              <Button type="button" onClick={() => setShowRejectModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" color="red" loading={submittingAction}>
                Rechazar Reporte
              </Button>
            </div>
          </Form>
        </div>
      </BasicModal>
    </div>
  );
}
